import { call, put } from 'redux-saga/effects';
import api from '../../../../../../../services/api';
import {
  selectCompanyIDSaga,
  selectCompanySettingsSaga,
  selectUserRoleSaga,
  selectUserSaga,
} from '../../../../../../../common/saga/selectors';
import { scheduleActions } from '../../actions';
import { AircraftTimelineEntry } from '../../../../../../../common/types/timeline/AircraftTimelineEntry';
import { AircraftTimelineEntryType } from '../../../../../../../common/types/timeline/AircraftTimelineEntryType';
import { CrewTimelineEntryType } from '../../../../../../../common/types/timeline/CrewTimelineEntryType';
import { entryTypeInStateMap } from '../../../types/state';
import {
  selectAircraftScheduleTimelineEntriesSaga,
  selectAllEntriesSagas,
  selectMXAircraftScheduleTLEntriesSaga,
} from '../../selectors-saga';
import _ from 'lodash';
import { Issue } from '../../../../../../../common/types/CombinedEntry';
import { CrewScheduleTimelineEntry } from '../../../../../../../common/types/timeline/CrewScheduleTimelineEntry';
import {
  selectCompanyScheduleVersionSaga,
  selectPreferredVersionSaga,
  selectPublishedVersionsSaga,
} from '../../../components/Versioning/redux-saga/selectors-saga';
import moment from 'moment-timezone';
import { ContractTimelineEntryType } from '../../../../../../../common/types/timeline/ContractTimelineEntryType';
import { MaintenanceTimelineEntryType } from '../../../../../../../common/types/timeline/MaintenanceTimelineEntryType';
import { MaintenanceAircraftTimelineEntry } from '../../../../../../../common/types/timeline/MaintenanceAircraftTimelineEntry';

export function* fetchTimelineEntries(
  from: string,
  to: string,
  entryType:
    | CrewTimelineEntryType
    | AircraftTimelineEntryType
    | ContractTimelineEntryType
    | MaintenanceTimelineEntryType,
  append: string = 'none',
  referenceDate: number = 0,
) {
  const userRoles = yield* selectUserRoleSaga();
  const companyID = yield* selectCompanyIDSaga();
  const companySettings = yield* selectCompanySettingsSaga();
  const publishedVersions = yield* selectPublishedVersionsSaga();
  const preferredVersion = yield* selectPreferredVersionSaga();
  const companyScheduleVersion = yield* selectCompanyScheduleVersionSaga();

  const baseApiParams = {
    end_time__gt: from,
    start_time__lt: to,
    limit: -1,
    user_ids: [],
  };

  if (
    companySettings?.Versioning &&
    publishedVersions?.length > 0 &&
    preferredVersion &&
    preferredVersion !== 'latest' &&
    companyScheduleVersion
  ) {
    let selectedVersion = preferredVersion;
    if (preferredVersion === 'current') {
      selectedVersion = String(companyScheduleVersion.CurrentVersion);
    }
    const selectedPublishedVersion = publishedVersions?.find(
      pv => pv.Version === parseInt(selectedVersion, 10),
    );

    if (
      selectedPublishedVersion?.VisibleStart &&
      moment(selectedPublishedVersion?.VisibleStart).valueOf() > moment(from).valueOf()
    ) {
      baseApiParams.end_time__gt = moment(selectedPublishedVersion?.VisibleStart).format();
    }

    if (
      selectedPublishedVersion?.VisibleEnd &&
      moment(selectedPublishedVersion?.VisibleEnd).valueOf() < moment(to).valueOf()
    ) {
      baseApiParams.start_time__lt = moment(selectedPublishedVersion?.VisibleEnd).format();
    }
  }

  if (
    !userRoles.includes('admin') &&
    !userRoles.includes('owner') &&
    !companySettings.PilotCanViewAllPilotsCrewSchedule &&
    !userRoles.includes('crewScheduleViewer') &&
    !userRoles.includes('taggedScheduleViewer') &&
    !userRoles.includes('subContractor')
  ) {
    const user = yield* selectUserSaga();
    baseApiParams.user_ids = [user.id];
  }

  try {
    const {
      data: { Data },
    } = yield call(api.get, `/v1/companies/${companyID}/timeline/${entryType}`, {
      params: baseApiParams,
    });

    if (entryType === AircraftTimelineEntryType.AircraftSchedule) {
      const entriesArr = (Data || []) as AircraftTimelineEntry[];

      if (append !== 'none') {
        const oldItems = yield* selectAircraftScheduleTimelineEntriesSaga();
        const newData = appendData(append, Data, oldItems, referenceDate, 'VirtualID');
        yield put(scheduleActions.setState({ aircraftScheduleTimelineEntries: newData }));
      } else {
        yield put(scheduleActions.setState({ aircraftScheduleTimelineEntries: entriesArr }));
      }
    } else if (entryType === AircraftTimelineEntryType.MaintenanceAircraftSchedule) {
      const entriesArr = (Data || []) as MaintenanceAircraftTimelineEntry[];
      if (append !== 'none') {
        const oldItems = yield* selectMXAircraftScheduleTLEntriesSaga();
        const newData = appendData(append, Data, oldItems.slice(), referenceDate, 'VirtualID');
        yield put(scheduleActions.setState({ mxAircraftScheduleTimelineEntries: newData }));
      } else {
        yield put(scheduleActions.setState({ mxAircraftScheduleTimelineEntries: entriesArr }));
      }
    } else {
      const stateKey = entryTypeInStateMap[entryType];
      const entriesArr = Data || [];

      if (append !== 'none') {
        const allEntries = yield* selectAllEntriesSagas();
        const oldItems = allEntries[entryType];
        const newData = appendData(append, entriesArr, oldItems, referenceDate, 'ID');
        yield put(scheduleActions.setState({ [stateKey]: newData }));
      } else {
        if (entryType === CrewTimelineEntryType.CrewSchedule) {
          const issues: { [userID: string]: Issue[] } = {};
          entriesArr.forEach((entry: CrewScheduleTimelineEntry) => {
            if (entry.Issues) {
              issues[entry.UserID] = entry.Issues;
            }
          });
          yield put(scheduleActions.setState({ crewScheduleViolations: issues }));
        }
        yield put(scheduleActions.setState({ [stateKey]: entriesArr }));
      }
    }
  } catch (err) {
    console.log(`Error fetching ${entryType} entries: `, err);
  }
}

const appendData = (append, newItems, oldItems, referenceDate, uniqueKey) => {
  if (!newItems) {
    return oldItems;
  }

  let result = [];
  if (append === 'left') {
    const truncatedOldItems = oldItems.filter(item => {
      return compareDateStrings(item.StartTime, referenceDate) === -1;
    });
    result = _.unionBy(newItems, truncatedOldItems, uniqueKey);
  } else if (append === 'right') {
    const truncatedOldItems = oldItems.filter(item => {
      return compareDateStrings(item.EndTime, referenceDate) === 1;
    });
    result = _.unionBy(newItems, truncatedOldItems, uniqueKey);
  }
  return result;
};

function compareDateStrings(dateString1, dateString2) {
  const date1 = new Date(dateString1);
  const date2 = new Date(dateString2);

  if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
    return 'Invalid date strings';
  }

  if (date1 < date2) {
    return -1; // dateString1 is earlier
  } else if (date1 > date2) {
    return 1; // dateString1 is later
  } else {
    return 0; // The dates are equal
  }
}
